// src/services/api.js
import axios from 'axios';
import { auth } from '../firebase';
import { getSltViewAs } from '../utils/sltViewAs';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api',
});

api.interceptors.request.use(async (config) => {
  const user = auth.currentUser;
  if (user) {
    try {
      const token = await user.getIdToken();
      config.headers.Authorization = `Bearer ${token}`;
    } catch (error) {
      console.error('Error getting Firebase ID token:', error);
      return Promise.reject(error);
    }
  }

  // SLT read-only exploration of a teacher's workspace. Never applied to auth
  // endpoints (the SLT user must stay themselves) or the SLT API itself.
  const viewAs = getSltViewAs();
  const url = String(config.url || '');
  if (viewAs?.teacherUid && !url.startsWith('/auth') && !url.startsWith('/slt')) {
    config.headers['X-SLT-View-As'] = viewAs.teacherUid;
  }

  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && auth.currentUser && originalRequest && !originalRequest._retryWithFreshToken) {
      originalRequest._retryWithFreshToken = true;

      try {
        const freshToken = await auth.currentUser.getIdToken(true);
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${freshToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        console.error('Unable to refresh Firebase session token:', refreshError);
      }
    }

    if (error.response?.status === 401) {
      console.error('Authentication request failed. The session may need to be refreshed.');
    }

    return Promise.reject(error);
  }
);

export const authAPI = {
  signup: (data) => {
    return api.post('/auth/signup', data);
  },
  getProfile: () => api.get('/auth/me'),
  recordGuideLogin: () => api.post('/auth/guide-login'),
  getImpersonationUsers: () => api.get('/auth/impersonation-users'),
  createImpersonationToken: (data) => api.post('/auth/impersonate', data),
  submitOnboardingSurvey: (data) => api.post('/auth/onboarding-survey', data),
  updateSettings: (data) => api.patch('/auth/settings', data),
};

export const lessonPlansAPI = {
  getAll: () => api.get('/lesson-plans'),
  getById: (id) => api.get(`/lesson-plans/${id}/documents`),
  create: (data) => api.post('/lesson-plans', data),
  update: (id, data) => api.patch(`/lesson-plans/${id}`, data),
  duplicate: (id, newTitle) => api.post(`/lesson-plans/${id}/duplicate`, { newTitle }),
  delete: (id) => api.delete(`/lesson-plans/${id}`),
};

export const studentsAPI = {
  count: () => api.get('/students/count'),
};

export const uploadAPI = {
  uploadFiles: (formData) => api.post('/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

export const reportAPI = {
  getExtractionReport: (id) => api.get(`/reports/extraction/${id}`),
};

export const documentAPI = {
    deleteById: (id) => api.delete(`/documents/${id}`),
};

export const homeworkScheduleAPI = {
  create: (data) => api.post('/homework-schedules', data),
  getAll: (lessonPlanId = null) => api.get('/homework-schedules', {
    params: lessonPlanId ? { lessonPlanId } : {}
  }),
  getById: (id) => api.get(`/homework-schedules/${id}`),
  deleteById: (id) => api.delete(`/homework-schedules/${id}`),
  rename: (id, name) => api.patch(`/homework-schedules/${id}`, { name }),
};

export const homeworkAPI = {
    create: (data) => api.post('/homeworks', data),
    getById: (id) => api.get(`/homeworks/${id}`),
    updateContentSelections: (id, data) => api.patch(`/homeworks/${id}/content-selections`, data),
    updateDuration: (id, durationMinutes) => api.patch(`/homeworks/${id}/duration`, { durationMinutes }),
    deleteById: (id) => api.delete(`/homeworks/${id}`),
};

export const lessonAnalysisAPI = {
    triggerAnalysis: (lessonPlanId, model, lessonTag = null, correlationId = null, customPromptKey = null) => api.post('/lesson-analysis', { lessonPlanId, model, lessonTag, correlationId, customPromptKey }),
    getStatus: (lessonPlanId) => api.get(`/lesson-analysis/${lessonPlanId}`),
    getReport: (lessonPlanId) => api.get(`/lesson-analysis/report/${lessonPlanId}`),
    generatePrePostAssessment: (lessonPlanId, testType, model = null) => api.post(`/lesson-analysis/${lessonPlanId}/pre-post-assessment`, { testType, model }),
    generatePrePostAssessmentImages: (lessonPlanId, assessment, testType, promptModel = null, imageModel = null) => api.post(`/lesson-analysis/${lessonPlanId}/pre-post-assessment/images`, { assessment, testType, promptModel, imageModel })
};

export const tutorialAPI = {
    triggerGeneration: (homeworkId, model, correlationId = null) => api.post('/tutorials', { homeworkId, model, correlationId }),
    getForHomework: (homeworkId) => api.get(`/tutorials/${homeworkId}`),
};

export const tutorAPI = {
    startSession: (homeworkId, lessonPlanId) => api.post('/tutor/start-session', { homeworkId, lessonPlanId }),
    submitAnswer: (data) => api.post('/tutor/submit-answer', data),
};

export const masterSlideshowAPI = {
  triggerGeneration: (homeworkId, chunkIndex, textModel, imageModel) => 
    api.post(`/slideshows/${homeworkId}/${chunkIndex}`, { textModel, imageModel }),
    
  getSlideshow: (homeworkId, chunkIndex) => api.get(`/slideshows/${homeworkId}/${chunkIndex}`),
};

export const studentAPI = {
  create: (data) => api.post('/students', data),
  getForSchedule: (scheduleId) => api.get(`/students/schedule/${scheduleId}`),
  getLibrary: () => api.get('/students/library'),
  getClasses: (includeEmpty = false) => api.get(`/students/classes${includeEmpty ? '?includeEmpty=true' : ''}`),
  addExistingStudentToSchedule: (scheduleId, sourceStudentId) => api.post(`/students/schedule/${scheduleId}/add-existing-student`, { sourceStudentId }),
  addExistingClassToSchedule: (scheduleId, sourceScheduleId) => api.post(`/students/schedule/${scheduleId}/add-existing-class`, { sourceScheduleId }),
  updateScaffolding: (studentId, data) => api.patch(`/students/${studentId}/scaffolding`, data),
  deleteById: (studentId) => api.delete(`/students/${studentId}`),
  renumberSupplyTeacher: (scheduleId) => api.post(`/students/schedule/${scheduleId}/renumber`),
  bulkCreateProfile: (scheduleId, profileData, count) => api.post(`/students/schedule/${scheduleId}/bulk-profile`, { profileData, count }),
  bulkDelete: (scheduleId, studentIds) => api.delete(`/students/schedule/${scheduleId}/bulk-delete`, { data: { studentIds } }),
  bulkUpdateScaffolding: (scheduleId, studentIds, profileData, targetCount) => api.patch(`/students/schedule/${scheduleId}/bulk-scaffolding`, { studentIds, profileData, targetCount }),
};

export const studentTutorialAPI = {
    getAll: () => {
        return api.get('/student-tutorials');
    },
    getForHomework: (homeworkId) => {
        return api.get(`/student-tutorials/homework/${homeworkId}`);
    },
    triggerGeneration: (homeworkId, studentId, model, glossaryLanguageMode, correlationId = null, rememberGlossaryPreference = false, fillInGaps = false, showScore = true) => {
        return api.post(`/student-tutorials/homework/${homeworkId}/student/${studentId}`, { model, glossaryLanguageMode, correlationId, rememberGlossaryPreference, fillInGaps, showScore });
    },
    getById: (studentTutorialId) => {
        return api.get(`/student-tutorials/${studentTutorialId}`);
    }
};

export const studentSlideshowAPI = {
  getForStudentTutorial: (studentTutorialId) => {
    return api.get(`/student-slideshows/student-tutorial/${studentTutorialId}`);
  },
  triggerGeneration: (studentTutorialId, textModel, imageModel) => {
    return api.post(`/student-slideshows/student-tutorial/${studentTutorialId}`, { textModel, imageModel });
  },
  getSlideshowChunk: (studentTutorialId, chunkIndex) => {
    return api.get(`/student-slideshows/student-tutorial/${studentTutorialId}/chunk/${chunkIndex}`);
  }
};

export const costAPI = {
  getCosts: () => api.get('/costs'),
  getLessonPlanSummary: (lessonPlanId) => api.get(`/costs/lesson-plan-summary/${lessonPlanId}`),
  getHomeworkSummary: (homeworkId) => api.get(`/costs/homework-summary/${homeworkId}`),
  getUserSummary: () => api.get('/costs/user-summary'),
  getPricing: () => api.get('/costs/pricing'),
};

export const jcqAPI = {
  getStudents: () => api.get('/jcq/students'),
  getNNowProfile: (studentId) => api.get(`/jcq/student/${studentId}/nnow-profile`),
  getExtraTimeTests: (classId) => api.get('/jcq/extra-time-tests', { params: { classId } }),
  getExtraTimeReport: (classId) => api.get('/jcq/extra-time-report', { params: { classId } }),
  generateTest: (data) => api.post('/jcq/generate-test', data),
  saveTeacherComment: (attemptId, jcqTeacherComment) => api.patch(`/jcq/attempt/${attemptId}/comment`, { jcqTeacherComment }),
  generateExtendedWritingQuestions: (data) => api.post('/jcq/extended-writing-questions', data),
  saveExtendedWritingPrompt: (data) => api.patch('/jcq/extra-time-report/extended-writing-prompt', data),
  uploadExtendedWritingResponse: (studentTutorialId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/jcq/extended-writing-response/${studentTutorialId}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  saveExtendedWritingMarking: (studentTutorialId, data) => api.patch(`/jcq/extended-writing-response/${studentTutorialId}`, data),
};

export const sltAPI = {
  getOverview: () => api.get('/slt/overview'),
  getTeachers: (params) => api.get('/slt/teachers', { params }),
  getTeacherDetail: (teacherUid) => api.get(`/slt/teachers/${teacherUid}`),
  getStudents: (params) => api.get('/slt/students', { params }),
  getStudentDetail: (studentId) => api.get(`/slt/students/${studentId}`),
  getLessonPlanDocuments: (lessonPlanId) => api.get(`/slt/lesson-plans/${lessonPlanId}/documents`),
};

export const tutorialAttemptAPI = {
  saveTutorialAnswer: (data) => api.post('/tutorial-attempts', data),
  completeAttempt: (data) => api.patch('/tutorial-attempts/complete', data),
  getTutorialProgress: (tutorialId, studentId) => 
    api.get(`/tutorial-attempts/${tutorialId}/student/${studentId}?_t=${Date.now()}`),
  getAttemptsForStudentTutorial: (studentTutorialId) =>
    api.get(`/tutorial-attempts/tutorial/${studentTutorialId}?_t=${Date.now()}`),
  getAttemptById: (attemptId) =>
    api.get(`/tutorial-attempts/attempt/${attemptId}?_t=${Date.now()}`),
  getStudentHistory: (studentId) => 
    api.get(`/tutorial-attempts/student/${studentId}/history?_t=${Date.now()}`)
};


export const studentTutorialLinkAPI = {
  create: (studentTutorialId) => api.post(`/student-tutorial-links/${studentTutorialId}`),
  emailLink: (studentTutorialId, email, studentName) => api.post(`/student-tutorial-links/${studentTutorialId}/email-link`, { email, studentName }),
  getPublic: (token) => api.get(`/student-tutorial-links/public/${token}`),
  savePublicAttemptAnswer: (token, data) => api.post(`/student-tutorial-links/public/${token}/attempts`, data),
  completePublicAttempt: (token, data) => api.patch(`/student-tutorial-links/public/${token}/attempts/complete`, data),
  getClassStats: (homeworkId) => api.get(`/student-tutorial-links/homework/${homeworkId}/class-stats`),
  getDeliveryStatus: (homeworkId) => api.get(`/student-tutorial-links/homework/${homeworkId}/delivery-status`),
  getTutorialReport: (homeworkId) => api.get(`/student-tutorial-links/homework/${homeworkId}/tutorial-report`),
  approveReview: (studentTutorialId, followOnTask = '') => api.patch(`/student-tutorial-links/${studentTutorialId}/approve-review`, { followOnTask }),
  rejectReview: (studentTutorialId, reason) => api.patch(`/student-tutorial-links/${studentTutorialId}/reject-review`, { reason }),
};

// +++ NEW: Activity & Events API +++
export const activityAPI = {
    sendHeartbeat: () => api.post('/activity/heartbeat'),
    logEvent: (eventType, eventName, metadata = {}) => api.post('/activity/log', { eventType, eventName, metadata }),
    getSummary: () => api.get('/activity/summary')
};

// +++ NEW: Interest API for Demo Requests +++
export const interestAPI = {
    register: (data) => api.post('/interest/register', data)
};

export const homeworkClassLinkAPI = {
    create: (homeworkId) => api.post(`/homework-class-links/${homeworkId}`),
    getPublic: (token) => api.get(`/homework-class-links/public/${token}`),
};

export default api;

export const translationAPI = {
  getSupportedLanguages: () => api.get('/translations/languages'),
};

export const contentLibraryAPI = {
    list: (params) => api.get('/content-library', { params }),
    getMeta: () => api.get('/content-library/meta'),
    open: (id) => api.post(`/content-library/${id}/open`),
    preview: (id) => api.get(`/content-library/${id}/preview`),
    use: (id, data) => api.post(`/content-library/${id}/use`, data),
    addToDashboard: (id, data) => api.post(`/content-library/${id}/add-to-dashboard`, data),
    activateOakLesson: (data) => api.post(`/content-library/activate-oak-lesson`, data),
    generateOakStudent: (data) => api.post(`/content-library/generate-oak-student`, data),
};
